import { Grid } from '@mui/material';
import React, { useRef, useState } from 'react';
import CustomButton from './NewLayout/Button';
import CustomDialog from './NewLayout/Dialog';
import type { FileData } from 'api/types';

interface FileUploaderProps {
  onFileChange: (fileData: FileData) => void;
  acceptedFileTypes?: string;
  buttonLabel?: string;
  initialFileData?: FileData;
}

// FileUploader component that can be used independently
export const FileUploader: React.FC<FileUploaderProps> = ({
  onFileChange,
  acceptedFileTypes = 'image/png, image/jpeg, image/jpg, .pdf',
  buttonLabel = 'Choose file',
  initialFileData
}) => {
  const [fileData, setFileData] = useState<FileData>(
    initialFileData || {
      file: null,
      fileName: ''
    }
  );

  const fileUrlRef = useRef<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (fileUrlRef.current) {
      URL.revokeObjectURL(fileUrlRef.current);
    }

    const newFileData = {
      file: selectedFile,
      fileName: selectedFile ? selectedFile.name : 'No File Selected'
    };

    setFileData(newFileData);
    onFileChange(newFileData);
  };

  const openFileInNewTab = () => {
    if (fileData.file) {
      const fileUrl = URL.createObjectURL(fileData.file);
      fileUrlRef.current = fileUrl;
      window.open(fileUrl, '_blank');
    }
  };

  const renderFilePreview = () => {
    if (!fileData.file) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '20px'
          }}>
          <div
            style={{
              width: '30px',
              height: '30px',
              marginRight: '10px',
              backgroundColor: '#ccc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            📎
          </div>
          <span style={{ fontSize: '14px', color: '#555' }}>No File Selected</span>
        </div>
      );
    }

    const previewUrl = URL.createObjectURL(fileData.file);
    fileUrlRef.current = previewUrl;

    const isPdf = fileData.file.type === 'application/pdf';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          marginBottom: '20px',
          cursor: 'pointer'
        }}
        onClick={openFileInNewTab}
        title="Click to open in new tab">
        {isPdf ? (
          <div
            style={{
              width: '180px',
              height: '220px',
              border: '1px solid #ddd',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f9f9f9'
            }}>
            <div style={{ fontSize: '48px' }}>📄</div>
            <div style={{ marginTop: '10px', fontSize: '14px' }}>PDF Document</div>
            <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>Click to view</div>
          </div>
        ) : (
          <img
            alt="Preview"
            src={previewUrl}
            style={{
              width: '180px',
              height: '180px',
              objectFit: 'contain',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px'
            }}
          />
        )}

        <span style={{ textAlign: 'center', fontSize: '14px', color: '#555', marginTop: '8px' }}>
          {fileData.fileName}
        </span>
      </div>
    );
  };

  React.useEffect(
    () => () => {
      if (fileUrlRef.current) {
        URL.revokeObjectURL(fileUrlRef.current);
      }
    },
    []
  );

  return (
    <Grid container direction="column" alignItems="center">
      {renderFilePreview()}

      <Grid item>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
          <input
            style={{ display: 'none' }}
            type="file"
            name="file"
            id="file-input"
            onChange={handleFileChange}
            accept={acceptedFileTypes}
          />
          <CustomButton
            label={buttonLabel}
            onClick={() => document.getElementById('file-input')?.click()}
          />
        </div>
      </Grid>
    </Grid>
  );
};

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (fileData: FileData) => void;
  loading?: boolean;
  acceptedFileTypes?: string;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
}

// Modal component that uses the FileUploader
const FileUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  acceptedFileTypes = 'image/png, image/jpeg, image/jpg, .pdf',
  cancelButtonLabel = 'Cancel',
  confirmButtonLabel = 'Upload'
}) => {
  const [fileData, setFileData] = useState<FileData>({
    file: null,
    fileName: ''
  });

  const handleFileChange = (newFileData: FileData) => {
    setFileData(newFileData);
  };

  const handleConfirm = () => {
    onConfirm(fileData);
  };

  const resetFileData = () => {
    setFileData({ file: null, fileName: '' });
  };

  const handleClose = () => {
    resetFileData();
    onClose();
  };

  return (
    <CustomDialog
      title={'Upload Attachment'}
      open={open}
      maxWidth="xs"
      closeDialog={handleClose}
      loading={loading}
      content={
        <FileUploader onFileChange={handleFileChange} acceptedFileTypes={acceptedFileTypes} />
      }
      actions={
        <>
          <CustomButton onClick={handleClose} variant="outlined" label={cancelButtonLabel} />
          <CustomButton
            onClick={handleConfirm}
            label={confirmButtonLabel}
            disabled={!fileData.file}
          />
        </>
      }
    />
  );
};

export default FileUploadModal;
